// A deadline for every promise the diagnostics UI waits on.
//
// All of them end in the device KV, and on the web that is `indexedDB.open`
// (brain `storage/kv.ts`), which has NO `onblocked` handler: it never settles
// while another tab holds a blocked upgrade, and it hangs outright in Firefox
// private mode. `.catch()` covers a rejection, not a non-answer — so a control
// that merely awaits one sits disabled forever with nothing on screen to
// explain it, and "Reset to default", the escape hatch out of a dev-server
// override that broke the app, never reaches its reload.
//
// A promise that misses the deadline is treated exactly like a rejection: the
// caller says so and moves on. Nothing here cancels the underlying work — it
// cannot — it only stops the UI hanging on it.

/** Settled in time, or not. Distinguishes "no answer" from an answer of null. */
export type Settled<T> = { ok: true; value: T } | { ok: false };

/**
 * Storage that has not answered in this long is not going to in time to be
 * useful. Long enough for a cold IndexedDB open on a slow phone, short enough
 * that a user does not read the screen as frozen.
 */
export const DIAG_DEADLINE_MS = 4000;

export function withDeadline<T>(
  work: Promise<T>,
  ms: number = DIAG_DEADLINE_MS,
): Promise<Settled<T>> {
  return new Promise<Settled<T>>((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false }), ms);
    void work.then(
      (value) => {
        clearTimeout(timer);
        resolve({ ok: true, value });
      },
      () => {
        clearTimeout(timer);
        resolve({ ok: false });
      },
    );
  });
}
